import type { CSSProperties } from 'react';
import clsx from 'clsx';
import { ReactNode, useState } from 'react';
import { Icon, type IconTypes } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { useCollapsibleHeight } from '../../_utils/useCollapsibleHeight';

type InfoBoxSeverity = 'info' | 'success' | 'warn' | 'error';

const severityIcon: Record<InfoBoxSeverity, IconTypes> = {
    info: 'info',
    success: 'check_circle',
    warn: 'warning',
    error: 'error',
};

export interface InfoBoxProps {
    severity?: InfoBoxSeverity;
    children?: ReactNode;
    items?: string[];
    className?: string;
    style?: CSSProperties;
    /**
     * Icon size. Defaults to 'small'
     */
    iconSize?: 'small' | 'normal' | 'large';
    /**
     * Overrides the icon shown for the given severity.
     */
    icon?: IconTypes;
    /**
     * Optional bold heading rendered above the content.
     */
    title?: ReactNode;
    /**
     * Allows the message content to be expanded or collapsed.
     */
    collapsible?: boolean;
    /**
     * Starts a collapsible message with its content hidden. Defaults to true.
     */
    defaultCollapsed?: boolean;
}

export function InfoBox(props: InfoBoxProps): ReactNode {
    const {
        children,
        items,
        severity = 'info',
        iconSize = 'small',
        className,
        style,
        icon,
        title,
        collapsible = false,
        defaultCollapsed = true,
    } = props;
    const [isCollapsed, setIsCollapsed] = useState(
        collapsible && defaultCollapsed,
    );
    const { contentRef, style: contentStyle } =
        useCollapsibleHeight(isCollapsed);
    const boxClassName = clsx(
        'info-box',
        severity,
        collapsible ? 'collapsible' : null,
        collapsible && isCollapsed ? 'collapsed' : null,
        className,
    );
    const isContentVisible = !collapsible || !isCollapsed;
    const content = items
        ? items.map((item, index) => (
              <span key={index}>
                  {items.length > 1 ? `${index + 1}. ` : ''}
                  {item}
              </span>
          ))
        : children;

    const toggleCollapsed = (): void => {
        setIsCollapsed((collapsed) => !collapsed);
    };

    const collapsibleAriaLabel = isContentVisible
        ? 'Collapse information'
        : 'Expand information';

    return (
        <div
            className={boxClassName}
            style={style}
            aria-expanded={collapsible ? isContentVisible : undefined}
        >
            <Icon
                className={severity}
                name={icon ?? severityIcon[severity]}
                fontSize={iconSize}
            />
            <div
                className={clsx(
                    'info-box-content flex flex-column',
                    !collapsible ? 'gap-2' : null,
                )}
            >
                {title && <strong>{title}</strong>}
                {collapsible ? (
                    <div
                        className="info-box-collapsible-content"
                        style={contentStyle}
                    >
                        <div
                            className="info-box-collapsible-content-inner"
                            ref={contentRef}
                        >
                            {content}
                        </div>
                    </div>
                ) : (
                    content
                )}
            </div>
            {collapsible && (
                <Button
                    className="info-box-toggle"
                    outlined
                    rounded
                    iconSize="sm"
                    aria-label={collapsibleAriaLabel}
                    icon={
                        <Icon
                            name={
                                isContentVisible
                                    ? 'keyboard_arrow_up'
                                    : 'keyboard_arrow_down'
                            }
                        />
                    }
                    onClick={toggleCollapsed}
                />
            )}
        </div>
    );
}
